import { ApiHelper } from './ApiHelper';
import { AuthenticatedUser } from '../security/auth/AuthenticatedUser';
import { DistributorDto } from './dto/DistributorDto';
import { DistributorSkusDto } from './dto/DistributorSkusDto';
import { SkuDto } from './dto/SkuDto';
import { OrderDto } from './dto/OrderDto';
import { OrderItemDto } from './dto/OrderItemDto';
import { PurchaseOrderItemDto } from './dto/PurchaseOrderItemDto';
import { MultiVendorSkuDto } from './dto/MultiVendorSkuDto';
import { MultiVendorSkuRequestDto } from './dto/MultiVendorSkuRequestDto';


export class ShopifySupportApi {

    public static async getDistributors(authenticatedUser: AuthenticatedUser): Promise<DistributorDto[] | null> {

        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'distributors'), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            }
        })
            .then(async data => {
                if (data.ok) {
                    const response = await data.json();
                    if (response) {
                        return ShopifySupportApi.decodeDistributorsResponse(response);
                    }
                }
                console.log('Get distributors request failed')
                return null;
            })
            .catch(err => {
                console.log('Caught exception getting distributors');
                console.log(err);
                return null;
            });
    }

    private static decodeDistributorsResponse(json: any): DistributorDto[] {
        const response: DistributorDto[] = [];
        const distributors = Object(json)["distributors"];
        for (let key in distributors) {
            const distributor = distributors[key];
            const code = distributor["distributorCode"];
            const name = distributor["distributorName"];
            response.push(new DistributorDto(code, name));
        }
        return response;
    }

    public static async getSkusForDistributor(distributorCode: string, authenticatedUser: AuthenticatedUser): Promise<DistributorSkusDto | null> {

        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'skus') + `?id=${distributorCode}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            }
        })
            .then(async data => {
                if (data.ok) {
                    const response = await data.json();
                    if (response) {
                        return ShopifySupportApi.decodeSkusResponse(response);
                    }
                }
                console.log('Get skus request failed')
                return null;
            })
            .catch(err => {
                console.log('Caught exception getting skus');
                console.log(err);
                return null;
            });
    }

    private static decodeSkusResponse(json: any): DistributorSkusDto {
        const code = Object(json)["distributorCode"];
        const name = Object(json)["distributorName"];

        const distributorSkus: SkuDto[] = [];
        const skus = Object(json)["skus"];
        for (let key in skus) {
            const item = skus[key];
            const sku = item["sku"];
            const distributorCode = item["distributorCode"];
            const distributorSku = item["distributorSku"];
            const brand = item["brand"];
            const styleCode = item["styleCode"];
            const partId = item["partId"];
            const color = item["color"];
            const colorCode = item["colorCode"];
            const sizeCode = item["sizeCode"];
            const notes = item["notes"];
            distributorSkus.push(new SkuDto(sku, distributorCode, distributorSku, brand, styleCode, partId, color, colorCode, sizeCode, notes));
        }
        return new DistributorSkusDto(new DistributorDto(code, name), distributorSkus);
    }

    public static async createSku(skuDto: SkuDto, authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'create-sku'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify(skuDto)
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`New sku record created for ${skuDto.sku}`)
                    return true;
                }
                console.log(`New sku record not created for ${skuDto.sku}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception creating sku record for ${skuDto.sku}`);
                console.log(err);
                return false;
            });
    }

    public static async updateSku(originalSku: string, skuDto: SkuDto, authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'update-sku'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify({
                originalSku: originalSku,
                newSku: skuDto.sku,
                distributorSku: skuDto.distributorSku,
                brand: skuDto.brand,
                styleCode: skuDto.styleCode,
                partId: skuDto.partId,
                color: skuDto.color,
                colorCode: skuDto.colorCode,
                sizeCode: skuDto.sizeCode
            })
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`Sku record updated for ${originalSku}`)
                    return true;
                }
                console.log(`Sku record not updated for ${originalSku}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception updating sku record for ${originalSku}`);
                console.log(err);
                return false;
            });
    }


    public static async deleteSku(sku: string, authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'delete-sku'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify({
                sku: sku
            })
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`Sku record deleted for ${sku}`)
                    return true;
                }
                console.log(`Sku record not deleted for ${sku}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception deleting sku record for ${sku}`);
                console.log(err);
                return false;
            });
    }

    public static async getOrders(days: number, authenticatedUser: AuthenticatedUser): Promise<OrderDto[] | null> {

        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'orders') + `?days=${days}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            }
        })
            .then(async data => {
                if (data.ok) {
                    const response = await data.json();
                    if (response) {
                        return ShopifySupportApi.decodeOrdersResponse(response);
                    }
                }
                console.log('Get orders request failed')
                return null;
            })
            .catch(err => {
                console.log('Caught exception getting orders');
                console.log(err);
                return null;
            });
    }

    private static decodeOrdersResponse(json: any): OrderDto[] {
        const orders: OrderDto[] = [];

        const orderRecords = Object(json)["orders"];
        for (let key in orderRecords) {
            const orderRecord = orderRecords[key];
            const orderId = orderRecord["orderId"];
            const orderNumber = orderRecord["orderNumber"];
            const createdDateTime = orderRecord["createdDateTime"];
            const processStartDateTime = orderRecord["processStartDateTime"];
            const deliverToName = orderRecord["deliverToName"];
            const deliverToAddress1 = orderRecord["deliverToAddress1"];
            const deliverToAddress2 = orderRecord["deliverToAddress2"];
            const deliverToCity = orderRecord["deliverToCity"];
            const deliverToStateProv = orderRecord["deliverToStateProv"];
            const deliverToZipPC = orderRecord["deliverToZipPC"];
            const deliverToCountry = orderRecord["deliverToCountry"];
            const orderNotes = orderRecord["orderNotes"];

            const order = new OrderDto(orderId, orderNumber, createdDateTime, processStartDateTime,
                deliverToName, deliverToAddress1, deliverToAddress2, deliverToCity, deliverToStateProv, deliverToZipPC,
                deliverToCountry, orderNotes);

            const orderItems = orderRecord["orderItems"];
            if (orderItems != null) {
                order.orderItems.push(...ShopifySupportApi.decodeOrderItemsResponse(orderItems));
            }

            const poItems = orderRecord["purchaseOrderItems"];
            if (poItems != null) {
                order.purchaseOrderItems.push(...ShopifySupportApi.decodePurchaseOrderItemsResponse(poItems));
            }
            orders.push(order);
        }
        return orders;
    }

    private static decodeOrderItemsResponse(orderItemsResponse: any): OrderItemDto[] {
        const orderItems: OrderItemDto[] = [];
        for (let key in orderItemsResponse) {
            const orderItemRecord = orderItemsResponse[key];
            const lineItemId = orderItemRecord["lineItemId"];
            const quantity = orderItemRecord["quantity"];
            const sku = orderItemRecord["sku"];
            const name = orderItemRecord["name"];
            const position = orderItemRecord["position"];
            const shopifyName = orderItemRecord["shopifyName"];
            orderItems.push(new OrderItemDto(lineItemId, quantity, sku, name, position, shopifyName));
        }
        return orderItems;
    }

    private static decodePurchaseOrderItemsResponse(poItemsResponse: any): PurchaseOrderItemDto[] {
        const purchaseOrderItems: PurchaseOrderItemDto[] = [];
        for (let key in poItemsResponse) {
            const poItemRecord = poItemsResponse[key];
            const poNumber = poItemRecord["poNumber"];
            const distributor = poItemRecord["distributor"];
            const quantity = poItemRecord["quantity"];
            const sku = poItemRecord["sku"];
            const name = poItemRecord["name"];
            const brand = poItemRecord["brand"];
            const color = poItemRecord["color"];
            const style = poItemRecord["style"];
            const size = poItemRecord["size"];
            const submittedDateTime = poItemRecord["submittedDateTime"];
            const successDateTime = poItemRecord["successDateTime"];
            const failureDateTime = poItemRecord["failureDateTime"];
            purchaseOrderItems.push(new PurchaseOrderItemDto(poNumber, distributor, quantity, sku, name, brand, color,
                style, size, submittedDateTime, successDateTime, failureDateTime));
        }
        return purchaseOrderItems;
    }

    public static async addSkus(skuDtos: SkuDto[], authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'add-skus'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify(skuDtos)
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`New sku records created for ${skuDtos.map(skuDto => skuDto.sku).join(', ')}`)
                    return true;
                }
                console.log(`New sku record not created for ${skuDtos.map(skuDto => skuDto.sku).join(', ')}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception creating sku records for ${skuDtos.map(skuDto => skuDto.sku).join(', ')}`);
                console.log(err);
                return false;
            });
    }

    public static async getMmcOrder(orderId: string, authenticatedUser: AuthenticatedUser): Promise<string | null> {

        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'mmc-order') + `?orderId=${orderId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            }
        })
            .then(async data => {
                if (data.ok) {
                    const response = await data.json();
                    if (response) {
                        const body = Object(response)["body"];
                        if (body) {
                            return body;
                        }
                    }
                }
                console.log('Get MMC order request failed')
                return null;
            })
            .catch(err => {
                console.log('Caught exception getting skus');
                console.log(err);
                return null;
            });
    }

    public static async getMultiVendorSkus(authenticatedUser: AuthenticatedUser): Promise<MultiVendorSkuDto[] | null> {

        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'multi-vendor-skus'), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            }
        })
            .then(async data => {
                if (data.ok) {
                    const response = await data.json();
                    if (response) {
                        return ShopifySupportApi.decodeMultiVendorSkuResponse(response);
                    }
                }
                console.log('Get multi-vendor skus request failed')
                return null;
            })
            .catch(err => {
                console.log('Caught exception getting multi-vendor skus');
                console.log(err);
                return null;
            });
    }

    private static decodeMultiVendorSkuResponse(response: any): MultiVendorSkuDto[] {
        const records: MultiVendorSkuDto[] = [];
        const skuRecords = response.multiVendorSkus ?? [];

        for (const skuRecord of skuRecords) {
            const primarySku = skuRecord["primarySku"];
            const primarySkuDistributorCode = skuRecord["primarySkuDistributorCode"];
            const primarySkuName = skuRecord["primarySkuName"];
            const alternateSku1 = skuRecord["alternateSku1"];
            const alternateSku1DistributorCode = skuRecord["alternateSku1DistributorCode"];
            const alternateSku1Name = skuRecord["alternateSku1Name"];
            const alternateSku2 = skuRecord["alternateSku2"];
            const alternateSku2DistributorCode = skuRecord["alternateSku2DistributorCode"];
            const alternateSku2Name = skuRecord["alternateSku2Name"];
            records.push(new MultiVendorSkuDto(primarySku, primarySkuDistributorCode, primarySkuName,
                alternateSku1, alternateSku1DistributorCode, alternateSku1Name,
                alternateSku2, alternateSku2DistributorCode, alternateSku2Name));
        }
        return records;
    }


    public static async createMultiVendorSku(requestDto: MultiVendorSkuRequestDto, authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'create-multi-vendor-sku'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify(requestDto)
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`New Cascade Multi-Vendor Sku record created for ${requestDto.primarySku}`)
                    return true;
                }
                console.log(`New Cascade Multi-Vendor Sku record not created for ${requestDto.primarySku}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception creating Cascade Multi-Vendor Sku record for ${requestDto.primarySku}`);
                console.log(err);
                return false;
            });
    }

    public static async updateMultiVendorSku(requestDto: MultiVendorSkuRequestDto, authenticatedUser: AuthenticatedUser): Promise<boolean> {
        return fetch(ApiHelper.CreateApiUrl('ShopifySupport', 'update-multi-vendor-sku'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': ApiHelper.GetApiKey(),
                'Authorization': ApiHelper.GetBearer(authenticatedUser)
            },
            body: JSON.stringify(requestDto)
        })
            .then(async data => {
                if (data.ok) {
                    console.log(`Cascade Multi-Vendor Sku record updated for ${requestDto.primarySku}`)
                    return true;
                }
                console.log(`Cascade Multi-Vendor Sku record not updated for ${requestDto.primarySku}`)
                return false;
            })
            .catch(err => {
                console.log(`Caught exception updating Cascade Multi-Vendor Sku record for ${requestDto.primarySku}`);
                console.log(err);
                return false;
            });
    }
}
